import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, LifeBuoy, CheckCircle2 } from "lucide-react";

const TONE = {
  fixed: "bg-emerald-100 text-emerald-800",
  escalated: "bg-amber-100 text-amber-800",
  resolved: "bg-sky-100 text-sky-800",
};

export default function AdminSupport() {
  const [tickets, setTickets] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState("escalated");
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/support/tickets${filter ? `?status=${filter}` : ""}`);
      setTickets(data.tickets || []);
      setCounts(data.counts || {});
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load tickets");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const resolve = async (tid) => {
    const note = window.prompt("Optional note for the agent (what was done):") ?? "";
    setActing((a) => ({ ...a, [tid]: true }));
    try {
      await api.post(`/support/tickets/${tid}/resolve`, { note });
      toast.success("Ticket resolved — agent notified");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Resolve failed");
    } finally {
      setActing((a) => ({ ...a, [tid]: false }));
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-support">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-display text-lg font-semibold flex items-center gap-2"><LifeBuoy size={16} /> Support Agent Tickets</h3>
          <p className="text-xs text-muted-foreground mt-1">
            Agent-reported issues. The AI Support Agent auto-fixes known problems; only escalated tickets need you.
          </p>
        </div>
        <div className="flex gap-1 text-xs">
          {["escalated", "fixed", "resolved", ""].map((s) => (
            <button
              key={s || "all"}
              onClick={() => setFilter(s)}
              data-testid={`support-filter-${s || "all"}`}
              className={`px-2.5 py-1 rounded-sm border ${filter === s ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}
            >
              {s ? `${s} (${counts[s] ?? 0})` : "All"}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="py-10 text-center text-xs text-muted-foreground"><Loader2 className="inline animate-spin mr-1" size={12} /> Loading…</div>
      ) : tickets.length === 0 ? (
        <div className="py-10 text-center text-xs text-muted-foreground" data-testid="support-no-tickets">No tickets in this view.</div>
      ) : (
        <div className="space-y-2">
          {tickets.map((t) => (
            <div key={t.id} className="border border-border rounded-sm p-3 bg-card" data-testid={`admin-ticket-${t.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-sm ${TONE[t.status] || "bg-slate-100"}`}>{t.status}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-slate-100 font-mono">{t.category}</span>
                    <span className="text-xs font-medium">{t.user_name}</span>
                    <span className="text-[10px] font-mono text-muted-foreground">{t.user_email} · {t.role_label}</span>
                    <span className="text-[10px] text-muted-foreground ml-auto">{new Date(t.created_at).toLocaleString()}</span>
                  </div>
                  <div className="text-xs mb-1">&quot;{t.text}&quot;</div>
                  <div className="text-[11px] text-muted-foreground"><strong>AI diagnosis:</strong> {t.diagnosis}</div>
                  {(t.actions_taken || []).length > 0 && (
                    <ul className="text-[10px] text-muted-foreground mt-1 list-disc list-inside">
                      {t.actions_taken.map((a, i) => <li key={i}>{a}</li>)}
                    </ul>
                  )}
                  {t.admin_note && <div className="text-[11px] mt-1 text-sky-800">Admin note: {t.admin_note}</div>}
                </div>
                {t.status === "escalated" && (
                  <Button size="sm" className="rounded-sm bg-emerald-600 hover:bg-emerald-700" onClick={() => resolve(t.id)} disabled={acting[t.id]} data-testid={`resolve-ticket-${t.id}`}>
                    <CheckCircle2 size={13} className="mr-1" /> Mark resolved
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
